import React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Message } from 'rsuite'

import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import { StandardDescription } from '../../ui_reusable/StandardInputs'
import { StandardButton } from '../../ui_reusable/styles/Button.styles'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import useStandardToaster from '../../ui_reusable/StandardToaster'
// Icons
import chatgpt_icon from '../../../assets/ChatGPT_logo.svg'
import claude_icon from '../../../assets/claude-color.svg'
import gemini_icon from '../../../assets/google-gemini-icon.svg'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import {
  RadioProvider,
  StandardRadio,
} from '../../ui_reusable/StandardRadioButtons'
import { FormSelectPicker } from '../../ui_reusable/StandardSelectPickers'
import { PanelNormalIndent } from '../../ui_reusable/styles/PlacementMisc.styles'
import theme from '../../../styles/theme'
// Styled components
import {
  ConfigureContainer,
  AISettingsSection,
  AIOptionGroup,
  ContextTextareaContainer,
  ContextTitle,
  ButtonContainer,
  AILogoContainer,
  AITitle,
  AIDescription,
  RadioButtonKeeper,
  AIContainer,
  AIInfoContainer,
  ContextDescription,
  HStack,
  AITitleNoLogo,
  SettingsRadioContainer,
  SettingsSelectInfo,
  SettingsSelectLabel,
} from './styles/ConfigProjectSolution.styles'

import SelectableDatasetList from '../../comp_reusable/lists/SelectableDatasetList'
import { useClientContext } from '../../../context/ClientContext'

const ConfigProjectSolutionPage = () => {
  const { project: id, solution: solutionId } = useParams()
  const [projectSolution, setProjectSolution] = useState(null)
  const [LLM, setLLM] = useState('')
  const [personality, setPersonality] = useState('')
  const [targetGroup, setTargetGroup] = useState('') // Add state for target group
  const [strategicContext, setStrategicContext] = useState('')
  const [companyContext, setCompanyContext] = useState('')
  const [projectContext, setProjectContext] = useState('')
  const [industryContext, setIndustryContext] = useState('')
  const [competitiveContext, setCompetitiveContext] = useState('')
  const [, setTimeframe] = useState({
    from: new Date(),
    to: new Date(),
  })
  const [datasets, setDatasets] = useState([])
  const [datasetsTotal, setDatasetsTotal] = useState(0)
  const [datasetsSelected, setDatasetsSelected] = useState(0)
  const [datasetsPage, setDatasetsPage] = useState(1) // 1-based, matches `?page=`
  const [datasetsSearch, setDatasetsSearch] = useState('')
  const [datasetsSort, setDatasetsSort] = useState(null) // { key (backend field), direction } | null
  const [datasetsShowSelected, setDatasetsShowSelected] = useState(false)
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true)
  const [refresh, setRefresh] = useState(new Date().getTime())
  const { selectedClient } = useClientContext()
  const { pushToast } = useStandardToaster()

  // Create refs for each AI option outside of the map function
  const chatgptRef = React.useRef(null)
  const claudeRef = React.useRef(null)
  const geminiRef = React.useRef(null)
  const briefRef = React.useRef(null)
  const analyticalRef = React.useRef(null)
  const entrepreneurialRef = React.useRef(null)
  const topManagementRef = React.useRef(null)
  const marketingRef = React.useRef(null)
  const generalRef = React.useRef(null)

  // Create a map of AI option refs
  const aiOptionRefs = {
    chatgpt_4: chatgptRef,
    claude: claudeRef,
    gemini: geminiRef,
  }

  const personalityRefs = {
    brief: briefRef,
    analytical: analyticalRef,
    entrepreneurial: entrepreneurialRef,
  }

  const targetRefs = {
    top_management: topManagementRef,
    marketing: marketingRef,
    general: generalRef,
  }

  // NOTE(server-pagination): `?project=` + `?solution=` pages enabled
  // client grants, annotates `is_solution_driver`, and sets `meta.active`
  // to assigned drivers. Toggles use incremental `add/remove_driver_ids`.
  const DATASETS_PAGE_SIZE = 8
  const clientId =
    selectedClient?.id ?? projectSolution?.project?.client?.id

  useEffect(() => {
    if (!id || !solutionId) {
      return
    }

    const abortController = new AbortController()

    const getProjectSolution = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${solutionId}?include_drivers=false`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const res = await response.json()
        const data = res.data

        setProjectSolution(data)
        if (data?.ai_settings) {
          setLLM(data.ai_settings.llm || '')
          setPersonality(data.ai_settings.personality || '')
          setTargetGroup(data.ai_settings.target_group || '')
          setStrategicContext(data.ai_settings.strategic_context || '')
          setProjectContext(data.ai_settings.project_context || '')
          setCompanyContext(data.ai_settings.company_context || '')
          setIndustryContext(data.ai_settings.industry_context || '')
          setCompetitiveContext(data.ai_settings.competitive_context || '')
          setTimeframe(data.timeframe)
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch project solution:', error)
        }
      }
    }

    getProjectSolution()

    return () => {
      abortController.abort()
    }
  }, [id, solutionId])

  // Solution details rarely change with grid pagination — fetch independently
  // with its own AbortController so paging/searching drivers never aborts it
  // (previously a shared controller surfaced "signal is aborted without reason").
  useEffect(() => {
    if (!id || !solutionId) return
    const abortController = new AbortController()

    const getDatasetsPage = async () => {
      setIsLoadingDatasets(true)
      try {
        if (!clientId || !id || !solutionId) {
          setDatasets([])
          setDatasetsTotal(0)
          setDatasetsSelected(0)
          return
        }

        const params = new URLSearchParams({
          project: String(id),
          solution: String(solutionId),
          limit: String(DATASETS_PAGE_SIZE),
          page: String(datasetsPage),
        })
        if (datasetsSearch) {
          params.set('search', datasetsSearch)
        }
        if (datasetsShowSelected) {
          params.set('is_solution_driver', 'true')
        }
        if (datasetsSort) {
          params.set(
            'ordering',
            `${datasetsSort.direction === 'desc' ? '-' : ''}${datasetsSort.key}`,
          )
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/datasets?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages — step back and let the effect refire.
        if (response.status === 404 && datasetsPage > 1) {
          setDatasetsPage(datasetsPage - 1)
          return
        }

        const clientDrivers = await response.json()
        const pageGrants = clientDrivers.data || []
        setDatasetsTotal(clientDrivers.total ?? pageGrants.length ?? 0)
        setDatasetsSelected(clientDrivers.meta?.active ?? 0)
        setDatasets(
          pageGrants.map((grant) => ({
            dataset: grant.dataset,
            is_active: Boolean(grant.is_solution_driver),
          })),
        )
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching client datasets:', error)
          setDatasets([])
          pushToast(<Message type="error">Failed to load drivers.</Message>, {
            placement: 'topCenter',
            duration: 5000,
          })
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingDatasets(false)
        }
      }
    }

    getDatasetsPage()

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line
  }, [
    id,
    solutionId,
    clientId,
    refresh,
    datasetsPage,
    datasetsSearch,
    datasetsSort,
    datasetsShowSelected,
  ])

  const handleDatasetsPageChange = useCallback((nextPage) => {
    setDatasetsPage(nextPage)
  }, [])

  const handleDatasetsSearchChange = useCallback((term) => {
    setDatasetsPage(1)
    setDatasetsSearch(term)
  }, [])

  const handleDatasetsShowSelectedChange = useCallback((next) => {
    setDatasetsPage(1)
    setDatasetsShowSelected(next)
  }, [])

  // The grid emits its own column key; translate to the backend ordering field.
  const handleDatasetsSortChange = useCallback((nextSort) => {
    setDatasetsPage(1)
    setDatasetsSort(
      nextSort && nextSort.key === 'name'
        ? { ...nextSort, key: 'dataset__name' }
        : nextSort,
    )
  }, [])

  // Update the handleToggleSelect for individual driver selection.
  // Uses an incremental write (no full driver list needed) and updates the
  // visible page optimistically.
  const handleToggleDriver = async (driverId, newState) => {
    try {
      const body = newState
        ? { add_driver_ids: [driverId] }
        : { remove_driver_ids: [driverId] }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${solutionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      )

      if (response.status !== 200) {
        throw new Error('Failed to update solution driver')
      }

      setDatasets((prev) =>
        prev.map((item) =>
          item.dataset.id === driverId
            ? { ...item, is_active: newState }
            : item,
        ),
      )
      setDatasetsSelected((count) => count + (newState ? 1 : -1))
    } catch (error) {
      console.error('Error toggling driver:', error)
      throw error
    }
  }

  // Handler for multiple drivers (page-scoped: `drivers` holds the visible page).
  const handleToggleMultipleDrivers = async (drivers, targetState) => {
    try {
      const changed = drivers.filter((d) => d.is_active !== targetState)
      const modifiedDriverIds = changed.map((d) => d.dataset.id)
      if (modifiedDriverIds.length === 0) {
        return
      }

      const body = targetState
        ? { add_driver_ids: modifiedDriverIds }
        : { remove_driver_ids: modifiedDriverIds }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${solutionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      )

      if (response.status !== 200) {
        throw new Error('Failed to update solution drivers')
      }

      const modified = new Set(modifiedDriverIds)
      setDatasets((prev) =>
        prev.map((item) =>
          modified.has(item.dataset.id)
            ? { ...item, is_active: targetState }
            : item,
        ),
      )
      setDatasetsSelected(
        (count) => count + (targetState ? changed.length : -changed.length),
      )
    } catch (error) {
      console.error('Error selecting drivers:', error)
      throw error
    }
  }

  // Select/deselect every matching client grant across pages — one API call.
  const handleSelectAllToggle = useCallback(async () => {
    const allSelected =
      datasetsTotal > 0 && datasetsSelected === datasetsTotal
    const targetState = !allSelected

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v2/projects/${id}/solutions/${solutionId}/drivers/bulk-active/`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          is_active: targetState,
          search: datasetsSearch || '',
        }),
      },
    )
    if (!response.ok) {
      throw new Error('Failed to update solution drivers')
    }
    setRefresh(new Date().getTime())
    return targetState
  }, [id, solutionId, datasetsTotal, datasetsSelected, datasetsSearch])

  const handleUpdate = async (payload) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${solutionId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )

    if (response.status === 200) {
      pushToast(
        <Message type="success">
          {projectSolution.solution.name} settings updated successfully.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } else {
      pushToast(<Message type="error">Failed to update settings.</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    }
  }

  const handleUpdateAISettings = async () => {
    const ai_settings = {
      llm: LLM,
      personality,
      target_group: targetGroup,
      strategic_context: strategicContext,
      project_context: projectContext,
      company_context: companyContext,
      industry_context: industryContext,
      competitive_context: competitiveContext,
    }
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${solutionId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        method: 'PATCH',
        body: JSON.stringify({ ai_settings }),
      },
    )

    if (response.status === 200) {
      pushToast(
        <Message type="success">AI+ settings updated successfully.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } else {
      pushToast(
        <Message type="error">Failed to update AI+ settings.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const aiOptions = [
    {
      value: 'chatgpt_4',
      label: 'ChatGPT 4.0',
      icon: chatgpt_icon,
      description: "This is what it's good for and how you can use it.",
      inactive: false,
    },
    {
      value: 'claude',
      label: 'Claude',
      icon: claude_icon,
      description: "This is what it's good for and how you can use it.",
      inactive: true,
    },
    {
      value: 'gemini',
      label: 'Gemini',
      icon: gemini_icon,
      description: "This is what it's good for and how you can use it.",
      inactive: true, // Set Gemini to inactive
    },
  ]

  const personalityOptions = [
    {
      value: 'brief',
      label: 'Brief and to-the-point',

      description:
        'Professional and efficient. Prefers bulletpoints and easy-to-digest summaries.',
      inactive: false,
    },
    {
      value: 'analytical',
      label: 'Analytical and comprehensive',
      description:
        'Prefers full paragraphs. Emphasized reasoning and explanations.',
      inactive: false,
    },
    {
      value: 'entrepreneurial',
      label: 'Analytical and entrepreneurial',
      description:
        'Combines full paragraphs and bulletpoints. Emphasizes trends, opportunities and ideas',
      inactive: false, // Set Gemini to inactive
    },
  ]

  const targetOptions = [
    {
      value: 'top_management',
      label: 'Top management',

      description:
        'Focus on strategic perspectives, stronger emphasis on industry and company context',
      inactive: false,
    },
    {
      value: 'marketing',
      label: 'Marketing people',
      description:
        'Focus on marketing-related perspectives related to sales, customers and promotions, stronger emphasis on projects, strategic and competitve context.',
      inactive: false,
    },
    {
      value: 'general',
      label: 'General audience',
      description:
        'Broad focus, looks for valuable insights emphasising all contexts.',
      inactive: false, // Set Gemini to inactive
    },
  ]

  const baselineStabilityOptions = [
    {
      value: 'stable',
      label: 'Stable',
    },
    {
      value: 'moderate',
      label: 'Moderate',
    },
    {
      value: 'flexible',
      label: 'Flexible',
    },
  ]

  const handleAIContainerClick = (e, containerRef, inactive, optionType) => {
    if (inactive) return
    e.stopPropagation()

    if (containerRef && containerRef.current) {
      const radioElement = containerRef.current.querySelector(
        'input[type="radio"]',
      )

      if (radioElement) {
        radioElement.click()
      }
    }
  }

  return (
    <ConfigureContainer>
      {projectSolution && (
        <>
          <HeaderContainer>
            <SectionTitle>
              Configure {projectSolution.solution.name}
            </SectionTitle>
            <ClientProjectPicker disabled={true} />
          </HeaderContainer>

          <SimpleAccordion
            id={'configureSolutionPageAccordion'}
            defaultActiveKey={['settings', 'drivers', 'ai']}
          >
            {projectSolution.solution.name === 'IMPACT' && (
              <SimpleAccordion.Panel
                header={`${projectSolution.solution.name} settings`}
                eventKey="settings"
              >
                <RadioProvider
                  group="model_library"
                  initValue={projectSolution.model_library || 'xgboost_basic'}
                  onChange={(value) => handleUpdate({ model_library: value })}
                >
                  <SettingsRadioContainer>
                    <RadioButtonKeeper>
                      <StandardRadio
                        group="model_library"
                        value="xgboost_basic"
                      />
                    </RadioButtonKeeper>
                    <p>
                      XG Boost with auto-optimization (suitable for smaller
                      datasets)
                    </p>
                  </SettingsRadioContainer>
                  <SettingsRadioContainer>
                    <RadioButtonKeeper>
                      <StandardRadio
                        group="model_library"
                        value="xgboost_optimized"
                      />
                    </RadioButtonKeeper>
                    <p>XG Boost with Optuna</p>
                  </SettingsRadioContainer>
                </RadioProvider>
              </SimpleAccordion.Panel>
            )}
            {projectSolution.solution.name === 'OPTIMIZE' && (
              <SimpleAccordion.Panel
                header={`${projectSolution.solution.name} settings`}
                eventKey="settings"
              >
                <SettingsSelectLabel>
                  Basine constraint prior
                </SettingsSelectLabel>
                <FormSelectPicker
                  data={baselineStabilityOptions}
                  value={projectSolution.baseline_stability || 'moderate'}
                  onChange={(value) =>
                    handleUpdate({ baseline_stability: value || 'moderate' })
                  }
                  style={{ width: '24rem' }}
                  data-testid="baseline-stability-select"
                />
                <SettingsSelectInfo>
                  <div>Stable: Mature categories with stable baseline.</div>
                  <div>Flexible: Highly seasonal categories, startups.</div>
                </SettingsSelectInfo>
              </SimpleAccordion.Panel>
            )}

            <SimpleAccordion.Panel header="Select drivers" eventKey="drivers">
              <SelectableDatasetList
                clientId={clientId}
                data={datasets}
                loading={isLoadingDatasets}
                setLoading={() => {}}
                onToggleDriver={handleToggleDriver}
                onToggleMultipleDrivers={handleToggleMultipleDrivers}
                onSelectAllToggle={handleSelectAllToggle}
                onRefresh={() => setRefresh(new Date().getTime())}
                serverSide
                serverTotal={datasetsTotal}
                serverSelected={datasetsSelected}
                serverPage={datasetsPage}
                onPageChange={handleDatasetsPageChange}
                onSearchChange={handleDatasetsSearchChange}
                onSortChange={handleDatasetsSortChange}
                showSelectedOnly={datasetsShowSelected}
                onShowSelectedChange={handleDatasetsShowSelectedChange}
                serverSortableKeys={['name']}
                serverSearchableKeys={['name']}
              />
            </SimpleAccordion.Panel>

            {projectSolution?.ai_settings && (
              <SimpleAccordion.Panel header="AI+ settings" eventKey="ai">
                <PanelNormalIndent>
                  <AISettingsSection>
                    <HStack>
                      <ContextTitle
                        style={{ color: theme.colors.standardText }}
                      >
                        {'Select LLM'}
                      </ContextTitle>
                      <ContextDescription>
                        {'Choose the language model for AI+'}
                      </ContextDescription>
                    </HStack>
                    <AIOptionGroup>
                      <RadioProvider
                        group={'llm_group'}
                        initValue={LLM || ''}
                        onChange={(value) => setLLM(value)}
                      >
                        {aiOptions.map((option) => (
                          <AIContainer
                            key={option.value}
                            ref={aiOptionRefs[option.value]}
                            selected={LLM === option.value}
                            inactive={option.inactive}
                            onClick={(e) =>
                              handleAIContainerClick(
                                e,
                                aiOptionRefs[option.value],
                                option.inactive,
                              )
                            }
                          >
                            <RadioButtonKeeper>
                              <StandardRadio
                                group={'llm_group'}
                                value={option.value}
                                disabled={option.inactive}
                              />
                            </RadioButtonKeeper>
                            <AIInfoContainer>
                              <AILogoContainer>
                                <img src={option.icon} alt={option.label} />
                              </AILogoContainer>
                              <AITitle>{option.label}</AITitle>
                              <AIDescription>
                                {option.description}
                              </AIDescription>
                            </AIInfoContainer>
                          </AIContainer>
                        ))}
                      </RadioProvider>
                    </AIOptionGroup>

                    <HStack>
                      <ContextTitle
                        style={{ color: theme.colors.standardText }}
                      >
                        {'Select personality'}
                      </ContextTitle>
                      <ContextDescription>
                        {'Choose the personality for AI+'}
                      </ContextDescription>
                    </HStack>
                    <AIOptionGroup>
                      <RadioProvider
                        group={'personality_group'}
                        initValue={personality || ''}
                        onChange={(value) => setPersonality(value)}
                      >
                        {personalityOptions.map((option) => (
                          <AIContainer
                            key={option.value}
                            ref={personalityRefs[option.value]}
                            selected={personality === option.value}
                            inactive={option.inactive}
                            onClick={(e) =>
                              handleAIContainerClick(
                                e,
                                personalityRefs[option.value],
                                option.inactive,
                              )
                            }
                          >
                            <RadioButtonKeeper>
                              <StandardRadio
                                group={'personality_group'}
                                value={option.value}
                                disabled={option.inactive}
                              />
                            </RadioButtonKeeper>
                            <AIInfoContainer>
                              <AITitleNoLogo>{option.label}</AITitleNoLogo>
                              <AIDescription>
                                {option.description}
                              </AIDescription>
                            </AIInfoContainer>
                          </AIContainer>
                        ))}
                      </RadioProvider>
                    </AIOptionGroup>

                    <HStack>
                      <ContextTitle
                        style={{ color: theme.colors.standardText }}
                      >
                        {'Select target group'}
                      </ContextTitle>
                      <ContextDescription>
                        {'Choose the use case for AI+'}
                      </ContextDescription>
                    </HStack>
                    <AIOptionGroup>
                      <RadioProvider
                        group={'target_group'}
                        initValue={targetGroup || ''}
                        onChange={(value) => setTargetGroup(value)}
                      >
                        {targetOptions.map((option) => (
                          <AIContainer
                            key={option.value}
                            ref={targetRefs[option.value]}
                            selected={targetGroup === option.value}
                            inactive={option.inactive}
                            onClick={(e) =>
                              handleAIContainerClick(
                                e,
                                targetRefs[option.value],
                                option.inactive,
                              )
                            }
                          >
                            <RadioButtonKeeper>
                              <StandardRadio
                                group={'target_group'}
                                value={option.value}
                                disabled={option.inactive}
                              />
                            </RadioButtonKeeper>
                            <AIInfoContainer>
                              <AITitleNoLogo>{option.label}</AITitleNoLogo>
                              <AIDescription>
                                {option.description}
                              </AIDescription>
                            </AIInfoContainer>
                          </AIContainer>
                        ))}
                      </RadioProvider>
                    </AIOptionGroup>

                    {/* Context inputs */}
                    {[
                      {
                        title: 'Strategic context',
                        type: 'strategic',
                        value: strategicContext,
                        setValue: setStrategicContext,
                        description:
                          'Provide context about the strategic goals and objectives.',
                      },
                      {
                        title: 'Project context',
                        type: 'project',
                        value: projectContext,
                        setValue: setProjectContext,
                        description:
                          'Describe specific project details and requirements.',
                      },
                      {
                        title: 'Company context',
                        type: 'company',
                        value: companyContext,
                        setValue: setCompanyContext,
                        description:
                          'Include relevant information about the company and its operations.',
                      },
                      {
                        title: 'Industry context',
                        type: 'industry',
                        value: industryContext,
                        setValue: setIndustryContext,
                        description:
                          'What are the key features, drivers and trends of the industry?',
                      },
                      {
                        title: 'Competitive context',
                        type: 'competitive',
                        value: competitiveContext,
                        setValue: setCompetitiveContext,
                        description:
                          'Who and what do you compete against, and what is the current market situation?',
                      },
                    ].map((contextType) => (
                      <ContextTextareaContainer key={contextType.type}>
                        <HStack>
                          <ContextTitle>{contextType.title}</ContextTitle>
                          <ContextDescription>
                            {contextType.description}
                          </ContextDescription>
                        </HStack>
                        <StandardDescription
                          placeholder={'Enter comments...'}
                          rows={4}
                          value={contextType.value || ''}
                          onChange={(value) => contextType.setValue(value)}
                        />
                      </ContextTextareaContainer>
                    ))}

                    <ButtonContainer>
                      <StandardButton onClick={handleUpdateAISettings}>
                        Save settings
                      </StandardButton>
                    </ButtonContainer>
                  </AISettingsSection>
                </PanelNormalIndent>
              </SimpleAccordion.Panel>
            )}
          </SimpleAccordion>
        </>
      )}
    </ConfigureContainer>
  )
}

export default ConfigProjectSolutionPage
